import { Star, ExternalLink } from 'lucide-react';
import { useSite } from '../lib/SiteContentContext';

function StarRow({ n = 5, size = 'w-4 h-4' }) {
  const filled = Math.max(0, Math.min(5, Math.round(n)));
  return (
    <div className="flex items-center gap-0.5" aria-label={`${filled} out of 5 stars`}>
      {[...Array(5)].map((_, i) => (
        <span
          key={i}
          className={`grid place-items-center ${size} ${i < filled ? 'bg-[#00B67A]' : 'bg-gray-200'}`}
          aria-hidden="true"
        >
          <Star className="w-3 h-3 text-white fill-white" strokeWidth={0} />
        </span>
      ))}
    </div>
  );
}

function ReviewCard({ r, url }) {
  const initial = (r.name || '?').charAt(0).toUpperCase();
  const dateLabel = r.date || '';
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`tp-review-${r.id || r.name}`}
      className="group shrink-0 w-[280px] sm:w-[320px] md:w-[360px] bg-white border border-black/10 rounded-2xl p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink"
    >
      <div className="flex items-center justify-between">
        <StarRow n={r.rating} />
        <span className="text-xs text-black/50">{dateLabel}</span>
      </div>
      {r.title && (
        <div className="mt-3 font-heading text-base md:text-lg text-brand-ink uppercase tracking-wide">
          {r.title}
        </div>
      )}
      <p className="mt-2 text-sm text-black/70 line-clamp-4">{r.body}</p>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-brand-sun grid place-items-center font-heading text-sm text-brand-ink" aria-hidden="true">
            {initial}
          </div>
          <span className="text-sm font-semibold text-brand-ink">{r.name}</span>
        </div>
        {r.verified && (
          <span className="text-[10px] uppercase tracking-widest text-[#00B67A] font-bold">
            Verified
          </span>
        )}
      </div>
    </a>
  );
}

export default function TrustpilotReviews() {
  const { trustpilot } = useSite();
  const summary = trustpilot?.summary || {};
  const reviews = (trustpilot?.reviews || []).filter((r) => r.published !== false && r.rating >= 2);

  if (reviews.length === 0) return null;

  const url = summary.profileUrl || 'https://uk.trustpilot.com/review/sciencesorted.co.uk';
  const label = summary.label || 'See our reviews';
  const doubled = reviews.length > 2 ? [...reviews, ...reviews] : reviews;

  return (
    <section
      className="py-12 md:py-16 bg-white border-y border-black/10 overflow-hidden"
      aria-labelledby="trustpilot-heading"
      data-testid="trustpilot-section"
    >
      <div className="container">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:justify-between mb-6 md:mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#00B67A] grid place-items-center shrink-0" aria-hidden="true">
              <Star className="w-7 h-7 text-white fill-white" strokeWidth={0} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span id="trustpilot-heading" className="font-heading text-lg md:text-xl uppercase tracking-wider text-brand-ink">
                  Trustpilot
                </span>
                <span className="text-[#00B67A] font-bold text-base md:text-lg">{label}</span>
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <StarRow n={5} />
              </div>
            </div>
          </div>

          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 self-start md:self-auto text-sm font-semibold text-brand-ink hover:text-[#00B67A] transition-colors focus-visible:outline-none focus-visible:underline"
          >
            View all reviews <ExternalLink className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </div>

      <div className="relative">
        <div
          className="flex gap-4 md:gap-5 marquee-track pl-5 pr-5"
          style={{ width: 'max-content' }}
        >
          {doubled.map((r, i) => (
            <ReviewCard r={r} url={url} key={`${r.id}-${i}`} />
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-10 md:w-24 bg-gradient-to-r from-white to-transparent" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 md:w-24 bg-gradient-to-l from-white to-transparent" aria-hidden="true" />
      </div>
    </section>
  );
}
